import { Skeleton, SkeletonCircle } from '@/components/ui/skeleton';
import { useInvitationData, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useBulkInvitationItemsQuery } from '@/hooks/queries/use-bulk-invitation-items-query';
import { bulkInvitePusherService } from '@/services/bulk-invite-pusher-service';
import useBulkInviteProgressStore from '@/stores/bulk-invite-progress-store';
import type { WishlistCreator } from '@fluenceur/types';
import { Box, Button, Center, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { MailOut } from 'iconoir-react';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkRecapLine, type BulkRecapLineData } from './bulk-recap-line';

interface BulkProgressViewProps {
  onClose: ({ success }: { success?: boolean }) => void;
}

/**
 * Bulk send epilogue. While the batch processes it shows a live count; on Pusher
 * completion it fetches the per-recipient items and joins them (plus the
 * prepare-time skipped items) with the local wishlist creators for a recap. In
 * link mode each successful line carries a copyable invitation link; in email
 * mode it reads as "queued" (the real send is asynchronous). Closing mid-flight
 * hands the aggregate display to the global toast (ownership transfer).
 */
const BulkProgressView: React.FC<BulkProgressViewProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { target } = useInvitationData();
  const bulkProgress = useInvitationStoreSelector((state) => state.bulkProgress);
  const activeBatch = useBulkInviteProgressStore((state) => state.activeBatch);
  const deliveryByInvitationId = useBulkInviteProgressStore(
    (state) => state.deliveryByInvitationId
  );
  const clearBatch = useBulkInviteProgressStore((state) => state.clearBatch);
  const transferToToast = useBulkInviteProgressStore((state) => state.transferToToast);

  const isCompleted = activeBatch?.status === 'completed';
  const total = activeBatch?.total ?? 0;
  const processed = (activeBatch?.sent ?? 0) + (activeBatch?.failed ?? 0);

  // On unmount: a still-processing batch is handed to the global toast (so
  // progress keeps surfacing after the modal closes); a completed one is cleared
  // (the recap was the last thing to show it), even when the modal is dismissed
  // from the header rather than the recap's Close. Either way the recap is the
  // last consumer of the outreach events, so the subscription is torn down here.
  useEffect(
    () => () => {
      const store = useBulkInviteProgressStore.getState();
      const batch = store.activeBatch;
      if (!batch) return;
      if (batch.status === 'processing') store.transferToToast();
      else {
        bulkInvitePusherService.unsubscribe();
        store.clearBatch();
      }
    },
    [transferToToast]
  );

  const { data: items } = useBulkInvitationItemsQuery(bulkProgress?.batchId ?? null, {
    enabled: isCompleted,
  });

  const localCreators = useMemo(() => {
    const map = new Map<string, WishlistCreator>();
    if (target?.type === 'wishlist-bulk') {
      for (const creator of target.data) map.set(creator.id, creator);
    }
    return map;
  }, [target]);

  const displayNameFor = useCallback(
    (creator: WishlistCreator | undefined, fallback: string) =>
      creator?.fullName || creator?.username || creator?.email || fallback,
    []
  );

  /**
   * The subline of a failed or skipped line: the backend code translated,
   * falling back to the English text it ships alongside when the code carries
   * no translation yet. Same `errors.<CODE>` flow every other dashboard failure
   * surface uses.
   *
   * Keyed on `t` so switching the dashboard language re-runs the lines memo and
   * re-translates an open recap, instead of leaving it in the previous locale.
   */
  const reasonText = useCallback(
    (code: string | null | undefined, englishFallback: string | null) =>
      (code ? t(`errors.${code}`, { defaultValue: '' }) : '') || englishFallback,
    [t]
  );

  const lines: BulkRecapLineData[] = useMemo(() => {
    if (!bulkProgress) return [];
    const isEmail = bulkProgress.deliveryMode === 'email';
    const created: BulkRecapLineData[] = (items ?? []).map((item) => {
      const creator = item.wishlistItemId ? localCreators.get(item.wishlistItemId) : undefined;
      // Only a processed item reads as success: a batch that aborted early
      // leaves items pending, which must not render as queued/link.
      const processed = item.status === 'sent' && !item.errorMessage;
      // Email mode starts each processed line "sending" and resolves it live to
      // "sent"/"failed" from the outreach events (matched by invitation id).
      const live = item.invitationId ? deliveryByInvitationId[item.invitationId] : undefined;
      const status: BulkRecapLineData['status'] = !processed
        ? 'failed'
        : !isEmail
          ? 'link'
          : live?.status === 'unknown'
            ? 'unknown'
            : live?.status === 'failed'
              ? 'failed'
              : live?.status === 'sent'
                ? 'sent'
                : 'sending';
      return {
        key: item.id,
        displayName: displayNameFor(creator, item.firstName || item.email || item.id),
        avatarUrl: creator?.profilePictureUrl ?? null,
        status,
        invitationLink: item.invitationLink,
        message:
          // A live outreach failure carries its OWN code and message (the item
          // itself is `sent` — only the send failed), so it is translated the
          // same way rather than surfacing the backend English text as-is.
          live?.status === 'unknown'
            ? t('errors.E_EMAIL_SEND_OUTCOME_UNKNOWN')
            : live?.status === 'failed'
              ? (reasonText(live.errorCode, live.message ?? null) ??
                reasonText(item.errorCode, item.errorMessage))
              : reasonText(item.errorCode, item.errorMessage),
      };
    });
    const skipped: BulkRecapLineData[] = bulkProgress.skippedItems.map((skip, index) => {
      const creator = localCreators.get(skip.wishlistItemId);
      return {
        key: `skip-${skip.wishlistItemId}-${index}`,
        displayName: displayNameFor(creator, skip.wishlistItemId),
        avatarUrl: creator?.profilePictureUrl ?? null,
        status: 'skipped',
        invitationLink: null,
        message: reasonText(skip.reasonCode, skip.reason),
      };
    });
    return [...created, ...skipped];
  }, [bulkProgress, items, localCreators, deliveryByInvitationId, displayNameFor, reasonText, t]);

  // Every created invitation counts as a success for the header, regardless of
  // where its email delivery currently stands (sending/sent).
  const successCount = useMemo(
    () =>
      lines.filter(
        (line) => line.status === 'link' || line.status === 'sending' || line.status === 'sent'
      ).length,
    [lines]
  );

  const hasUnknown = lines.some((line) => line.status === 'unknown');

  const handleClose = () => {
    clearBatch();
    onClose({ success: true });
  };

  if (!isCompleted) {
    // Skeleton rows mirroring the recap lines, one per expected recipient
    // (capped so a large batch does not stretch the modal).
    const skeletonCount = Math.min(Math.max(total, 1), 5);
    return (
      <Flex direction="column" gap="4" py="2">
        <VStack align="stretch" gap="2" w="full">
          {Array.from({ length: skeletonCount }, (_, index) => (
            <HStack
              key={index}
              gap="2.5"
              borderWidth="1px"
              borderColor="cardBorder"
              rounded="lg"
              bg="panelBg"
              px="12px"
              py="2"
            >
              <SkeletonCircle size="6" flexShrink={0} />
              <Flex direction="column" gap="1.5" flex="1" minW="0" py="1">
                <Skeleton height="10px" width="120px" />
                <Skeleton height="8px" width="220px" maxW="70%" />
              </Flex>
            </HStack>
          ))}
        </VStack>
        <Flex direction="column" gap="3" align="center" textAlign="center">
          <Text fontSize="13px" color="fg.muted">
            {t('invitations.modal.channel.bulk.recap.sending', { current: processed, total })}
          </Text>
          <Button variant="outline" onClick={() => onClose({ success: true })}>
            {t('invitations.modal.channel.bulk.recap.continueInBackground')}
          </Button>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="4" align="center" textAlign="center" pt="2">
      <Center
        boxSize="48px"
        rounded="full"
        bg={hasUnknown ? 'orange.100' : 'green.100'}
        color={hasUnknown ? 'orange.700' : 'green.700'}
      >
        <MailOut width={22} height={22} strokeWidth={2.2} />
      </Center>
      <Box>
        <Text fontFamily="heading" fontSize="16px" fontWeight="semibold" color="dark">
          {hasUnknown
            ? t('bulkEmail.progress.unknown.title')
            : t('invitations.modal.channel.bulk.recap.title', { count: successCount })}
        </Text>
        <Text fontSize="12.5px" color="fg.muted" maxW="400px" mt="1.5" lineHeight="1.5">
          {hasUnknown
            ? t('errors.E_EMAIL_SEND_OUTCOME_UNKNOWN')
            : bulkProgress?.deliveryMode === 'link'
              ? t('invitations.modal.channel.bulk.recap.textLink')
              : t('invitations.modal.channel.bulk.recap.textEmail')}
        </Text>
      </Box>

      <VStack align="stretch" gap="2" w="full" maxH="320px" overflowY="auto" textAlign="left">
        {lines.map((line) => (
          <BulkRecapLine key={line.key} line={line} />
        ))}
      </VStack>

      <Flex justify="center" pt="1" pb="2">
        <Button colorPalette="primary" onClick={handleClose}>
          {t('common.close')}
        </Button>
      </Flex>
    </Flex>
  );
};

export default BulkProgressView;
